import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import AnalyticsBreadcrumb from "./AnalyticsBreadcrumb";
import {
  SET_RAW_DATA,
  SET_FILTERED_DATA,
  SET_FILTERED_LAST_DATA,
  SET_DATA_DATES,
  SET_NAVIGATION,
  SET_CURRENT_USER_GROUP,
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_GET_TASK_TEMPLATE_SELECT,
  SG_GET_DEBRIEF_SCHEDULES,
  SG_GET_CULTURE_AUDIT_REPORTS,
  SG_GET_ORGANIZATION_GROUP,
} from "constants/actions";
import { SURVEY_TYPE } from "constants/surveys";
import NoData from 'assets/images/UI/no_data.svg'

// Import calculation functions from LeaderDataLoader's dependencies
import {
  checkPersonality,
  convert_personality_to_categories,
} from "app/audit/calculations";

import {
  filter_data,
  get_dates,
  get_most_recent,
  build_default_State,
} from "reports/Audit/data_navigation/calculate_data";

import { get_last_responses2, sort_historical_data2, get_last_responses_group, sort_historical_data_group } from "./get_last_and_historical_cohort";

// Helper utilities for extracting numeric identifiers from various payload shapes
const extractNumericId = (value) => {
  if (value == null) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const id = extractNumericId(item);
      if (id != null) return id;
    }
    return null;
  }
  if (typeof value === "object") {
    if ("id" in value) return extractNumericId(value.id);
    if ("pk" in value) return extractNumericId(value.pk);
    if ("value" in value) return extractNumericId(value.value);
    return null;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric;
  }
  return null;
};

const pickFirstNumericId = (...candidates) => {
  for (const candidate of candidates) {
    const id = extractNumericId(candidate);
    if (id != null) return id;
  }
  return null;
};

const extractDisplayName = (value) => {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const name = extractDisplayName(item);
      if (name) return name;
    }
    return null;
  }
  if (typeof value === "object") {
    const candidates = [
      value.name,
      value.title,
      value.group_name,
      value.groupName,
      value.team_name,
      value.display_name,
      value.displayName,
      value.label,
    ];
    for (const candidate of candidates) {
      const name = extractDisplayName(candidate);
      if (name) return name;
    }
  }
  return null;
};

const pickFirstDisplayName = (...candidates) => {
  for (const candidate of candidates) {
    const name = extractDisplayName(candidate);
    if (name) return name;
  }
  return null;
};

const NewAnalytics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get initial tab from URL params (sanitize deprecated 'historical')
  const initialTabParam = searchParams.get('tab') || 'latest';
  const initialTab = initialTabParam === 'historical' ? 'latest' : initialTabParam;
  
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pulseSearchValue, setPulseSearchValue] = useState("");
  const [historySurveyName, setHistorySurveyName] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [isHistoryTableOpen, setIsHistoryTableOpen] = useState(false);
  const [historySortOrder, setHistorySortOrder] = useState(null);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState(new Set());
  const [historyPage, setHistoryPage] = useState(1);
  const historyItemsPerPage = 10;
  const historySelectAllRef = useRef(null);
  const [calcMethod, setCalcMethod] = useState('group'); // 'cohort' | 'group'
  
  // Debug: Track renders
  const renderCount = useRef(0);
  renderCount.current += 1;

  const {
    get_culture_audit_reports,
    get_survey_structure,
    get_organizations,
    survey_version,
    get_auth,
    organizationId,
    nav_state,
    outcomesQuestionsList,
    scheduledPulseSurveys,
    allPulseSurveys,
    get_debrief_schedules,
    organizationGroups,
    get_employee,
  } = useSelector(
    (state) => ({
      get_debrief_schedules: state.debrief_schedule,
      get_culture_audit_reports: state.debrief_schedule.culture_audit_reports,
      get_survey_structure: state.surveystructure.survey_structure,
      get_organizations: state.organizations,
      survey_version: state.audit.survey_version,
      get_auth: state.auth,
      organizationId: state.selectedOrg?.organization?.id || Number(state.auth.organization_id),
      nav_state: state.audit.nav,
      outcomesQuestionsList: state.surveyquestions.outcome_questions,
      scheduledPulseSurveys: state.task_templates.scheduled_tasks,
      allPulseSurveys: state.task_templates.task_templates,
      pulseFetched: state.task_templates.surveysFetched,
      organizationGroups: Array.isArray(state.organization_group?.organization_groups)
        ? state.organization_group.organization_groups
        : [],
      get_employee: state.employees,
    }),
    shallowEqual
    );

  // Track permission groups separately for manager detection
  const [permissionGroups, setPermissionGroups] = useState([]);

  

  // Debug: Log when selector values change
  const prevReports = useRef();
  const prevOrgs = useRef();
  
  if (prevReports.current !== get_culture_audit_reports) {
    prevReports.current = get_culture_audit_reports;
  }
  
  if (prevOrgs.current !== get_organizations) {
    prevOrgs.current = get_organizations;
  }


  // Handle initial load animation timing
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Load pulse survey data
  useEffect(() => {
    if (selectedTab === 'pulse') {
      dispatch({ type: SG_GET_TASK_TEMPLATE_SELECT });
      dispatch({ type: SG_GET_TASK_SCHEDULES });
      dispatch({ type: SG_GET_TASK_TEMPLATES });
    }
  }, [dispatch, selectedTab]);


  useEffect(() => {
    if (!organizationId) return;
    if (organizationGroups?.length) return;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        organization: organizationId,
        // group_type: "survey",
        // page: 1,
        // page_size: 500,
      },
    });
  }, [dispatch, organizationId, organizationGroups?.length]);

  // Track if we've attempted to fetch permission groups
  const [permissionGroupsFetched, setPermissionGroupsFetched] = useState(false);
  const permissionGroupsFetchDispatchedRef = useRef(false);
  const lastFetchedOrgIdRef = useRef(null);
  const lastUserGroupRef = useRef({ name: null, id: null });

  // Fetch permission groups for manager detection
  useEffect(() => {
    // Validate organizationId is a valid positive integer
    const orgId = Number(organizationId);
    if (!organizationId || isNaN(orgId) || orgId <= 0) {
      console.log("⏸️ Skipping permission groups fetch - invalid organizationId:", organizationId);
      // Still mark as fetched so culture audit can proceed
      if (!permissionGroupsFetched) {
        setPermissionGroupsFetched(true);
      }
      return;
    }

    // Only fetch if organizationId changed or we haven't fetched yet
    if (lastFetchedOrgIdRef.current === orgId && permissionGroupsFetched) {
      return;
    }

    lastFetchedOrgIdRef.current = orgId;
    setPermissionGroupsFetched(false);
    permissionGroupsFetchDispatchedRef.current = true;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: `?organization=${orgId}&group_type=permission&page=1&page_size=500`,
    });

    // Fallback: mark as fetched after 2 seconds if no response comes back
    const timeoutId = setTimeout(() => {
      setPermissionGroupsFetched((prev) => {
        if (!prev) {
          console.log("⏱️ Permission groups fetch timeout - proceeding with culture audit");
          permissionGroupsFetchDispatchedRef.current = false;
          return true;
        }
        return prev;
      });
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [dispatch, organizationId]);

  // Store permission groups when they arrive in Redux
  useEffect(() => {
    const groups = Array.isArray(organizationGroups) ? organizationGroups : [];
    const permGroups = groups.filter(g => g?.group_type === "permission");

    // Always update permission groups state
    setPermissionGroups(permGroups);

    // Mark as fetched if we dispatched a permission groups fetch and got a response
    if (permissionGroupsFetchDispatchedRef.current) {
      setPermissionGroupsFetched((prev) => {
        if (!prev) {
          permissionGroupsFetchDispatchedRef.current = false;
          console.log("📋 Permission groups processed:", permGroups.length, "permission groups found out of", groups.length, "total groups");
          return true;
        }
        return prev;
      });
    }
  }, [organizationGroups]);

  const organizationGroupNameMap = useMemo(() => {
    const map = new Map();

    const list = Array.isArray(organizationGroups) ? organizationGroups : [];
    list.forEach((group) => {
      const id = Number(group?.id);
      if (Number.isFinite(id) && id > 0) {
        const name = group?.name || group?.title || group?.group_name || group?.team_name;
        if (name) {
          map.set(id, name);
        }
      }
    });

    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];

    responses.forEach((response) => {
      const groupObj = response?.organization_group;
      const id = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group_id,
        response?.team_id
      );
      if (id == null) return;

      const name =
        response?.name ||
        groupObj?.name ||
        response?.organization_group_name ||
        response?.group_name ||
        response?.team_name ||
        response?.name;
      if (name) {
        map.set(id, name);
      }
    });

    return map;
  }, [organizationGroups, get_culture_audit_reports?.response]);

  const organizationGroupDataMap = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(organizationGroups) ? organizationGroups : [];
    list.forEach((group) => {
      const id = Number(group?.id);
      if (Number.isFinite(id) && id > 0) {
        map.set(id, group);
      }
    });
    return map;
  }, [organizationGroups]);

  const getCurrentUserGroup = useCallback(() => {
    const currentEmployeeId = extractNumericId(
      get_auth?.employee_id ||
      get_auth?.employee?.id ||
      get_employee?.userEmp?.id ||
      get_auth?.id
    );

    const getMembersCountFromGroup = (group) => {
      const candidates = [
        group?.members_count,
        group?.member_count,
        Array.isArray(group?.members) ? group.members.length : null,
      ];
      for (const candidate of candidates) {
        const num = Number(candidate);
        if (Number.isFinite(num) && num >= 0) {
          return num;
        }
      }
      return null;
    };

    const findGroupById = (id) => {
      if (id == null) return null;
      const numericId = Number(id);
      if (!Number.isFinite(numericId)) return null;
      return (
        organizationGroupDataMap.get(numericId) ||
        permissionGroups.find((g) => Number(g?.id) === numericId) ||
        null
      );
    };

    const managerMatchFrom = (groups) => {
      if (!Array.isArray(groups) || !groups.length) return null;
      const match = groups.find((group) => {
        const managerId = extractNumericId(group?.manager?.id);
        return managerId != null && managerId === currentEmployeeId;
      });
      return match || null;
    };

    const resolveNameFromGroup = (group) =>
      pickFirstDisplayName(
        group?.name,
        group?.title,
        group?.group_name,
        group?.team_name
      );

    const findGroupIdByName = (name) => {
      if (!name) return null;
      const lowerName = `${name}`.toLowerCase();

      for (const [id, storedName] of organizationGroupNameMap.entries()) {
        if (!storedName) continue;
        const normalized = `${storedName}`.toLowerCase();
        if (storedName === name || normalized === lowerName) {
          return id;
        }
      }

      const permissionMatch = permissionGroups.find((g) => {
        const groupName =
          g?.name ||
          g?.title ||
          g?.group_name ||
          g?.team_name ||
          g?.display_name;
        if (!groupName) return false;
        return `${groupName}`.toLowerCase() === lowerName;
      });

      return permissionMatch ? extractNumericId(permissionMatch?.id) : null;
    };

    const orgManagerGroup = managerMatchFrom(organizationGroups);
    if (orgManagerGroup) {
      const memberCount = getMembersCountFromGroup(orgManagerGroup);
      return {
        id: extractNumericId(orgManagerGroup?.id),
        name: resolveNameFromGroup(orgManagerGroup),
        memberCount,
      };
    }

    const permManagerGroup = managerMatchFrom(permissionGroups);
    if (permManagerGroup) {
      const memberCount = getMembersCountFromGroup(permManagerGroup);
      return {
        id: extractNumericId(permManagerGroup?.id),
        name: resolveNameFromGroup(permManagerGroup),
        memberCount,
      };
    }

    const user = get_employee?.userEmp || get_auth?.employee || get_auth;

    const directName = pickFirstDisplayName(
      user?.group_name,
      user?.groupName,
      user?.team_name,
      user?.teamName,
      user?.organization_group_name,
      user?.organizationGroupName,
      user?.permission_group_name,
      user?.group
    );
    if (directName) return directName;

    const groupCandidates = [
      user?.groups,
      user?.organization_groups,
      user?.permission_groups,
      user?.employee_groups,
      user?.employee_employee_permission?.permission_groups,
      user?.employee_employee_permission?.groups,
    ];

    for (const candidate of groupCandidates) {
      const groupValue = Array.isArray(candidate) ? candidate[0] : candidate;
      if (!groupValue) continue;

      const id = extractNumericId(groupValue);
      const name = pickFirstDisplayName(
        groupValue?.name,
        groupValue?.title,
        groupValue?.group_name,
        groupValue?.team_name,
        groupValue?.display_name
      );

      const resolvedName =
        name ||
        organizationGroupNameMap.get(id) ||
        permissionGroups.find((g) => Number(g?.id) === Number(id))?.name ||
        null;

      const groupData = findGroupById(id) || findGroupById(findGroupIdByName(resolvedName));
      const memberCount = getMembersCountFromGroup(groupData);

      if (resolvedName || id != null) {
        return {
          id: id ?? findGroupIdByName(resolvedName),
          name: resolvedName,
          memberCount,
        };
      }
    }

    const permissionGroupId = extractNumericId(user?.employee_employee_permission?.group);
    if (permissionGroupId != null) {
      const mappedName = organizationGroupNameMap.get(permissionGroupId)
        || permissionGroups.find((g) => Number(g?.id) === Number(permissionGroupId))?.name;
      if (mappedName) {
        const groupData = findGroupById(permissionGroupId);
        return {
          id: permissionGroupId,
          name: mappedName,
          memberCount: getMembersCountFromGroup(groupData),
        };
      }
    }

    const permissionGroupName = pickFirstDisplayName(
      user?.employee_employee_permission?.group_name,
      user?.employee_employee_permission?.permission_group_name
    );
    if (permissionGroupName) {
      const groupData = findGroupById(findGroupIdByName(permissionGroupName));
      return {
        id: findGroupIdByName(permissionGroupName),
        name: permissionGroupName,
        memberCount: getMembersCountFromGroup(groupData),
      };
    }

    return {
      id: null,
      name: null,
      memberCount: null,
    };
  }, [get_auth, get_employee, organizationGroupDataMap, organizationGroupNameMap, organizationGroups, permissionGroups]);

  const yourGroup = useMemo(() => getCurrentUserGroup(), [getCurrentUserGroup]);
  const yourGroupName = yourGroup?.name;
  const yourGroupId = yourGroup?.id;
  const yourGroupCount = yourGroup?.memberCount;

  // Make dependencies more stable by using length/properties instead of objects
  const reportsLength = get_culture_audit_reports?.response?.length || 0;
  const hasOrganizations = !!get_organizations;

  const scheduleResults = Array.isArray(get_debrief_schedules?.debrief_schedule?.results)
    ? get_debrief_schedules.debrief_schedule.results
    : [];

  const scheduleMapById = useMemo(() => {
    return scheduleResults.reduce((map, sched) => {
      const schedId = extractNumericId(sched?.id);
      if (schedId != null) {
        map.set(schedId, sched);
      }
      return map;
    }, new Map());
  }, [scheduleResults]);

  const scheduleToGroupIdMap = useMemo(() => {
    const map = new Map();

    scheduleResults.forEach((sched) => {
      const schedId = extractNumericId(sched?.id);
      const groupId = sched?.organization_group;

      if (schedId == null || groupId == null) return;

      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const groupName =
        groupNameFromMap ||
        sched?.group_name ||
        null;

      map.set(schedId, {
        groupId,
        groupName,
      });
    });

    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];

    responses.forEach((response) => {
      const schedId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );
      const groupObj = response?.organization_group;
      const groupId = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );
      if (schedId == null || groupId == null) return;

      const existing = map.get(schedId);
      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const candidateName =
        groupNameFromMap ||
        existing?.groupName ||
        groupObj?.name ||
        response?.organization_group_name ||
        response?.group_name ||
        response?.team_name ||
        response?.name ||
        null;

      map.set(schedId, {
        groupId,
        groupName: candidateName,
      });
    });

    return map;
  }, [get_culture_audit_reports?.response, scheduleResults, organizationGroupNameMap]);

  // Ensure culture audit reports are loaded for mapping schedule/group metadata
  useEffect(() => {
    // Validate organizationId is a valid positive integer
    const orgId = Number(organizationId);
    if (!organizationId || isNaN(orgId) || orgId <= 0) {
      console.log("⏸️ Skipping culture audit call - invalid organizationId:", organizationId);
      return;
    }

    const hasReports = Array.isArray(get_culture_audit_reports?.response)
      && get_culture_audit_reports.response.length > 0;
    if (hasReports) return;

    // Wait for permission groups to be fetched before making the call
    if (!permissionGroupsFetched) {
      console.log("⏳ Waiting for permission groups to be fetched...");
      return;
    }

    console.log("✅ Permission groups fetched, proceeding with culture audit call");

    // Derive manager group ID for non-admin managers
    const userEmployee = get_employee?.userEmp;
    const userAdmin = userEmployee?.account_type === 5;
    const isManager = !userAdmin && !!userEmployee;

    console.log("🔍 NewAnalytics - Manager detection:", {
      userEmployee: userEmployee?.id,
      userAdmin,
      isManager,
      permissionGroupsCount: permissionGroups?.length,
      get_employee
    });

    let managerGroupId = null;
    if (isManager && permissionGroups.length > 0) {
      const match = permissionGroups.find((g) => {
        if (!g) return false;
        if (g.organization && Number(g.organization) !== Number(organizationId)) {
          return false;
        }
        const manager = g.manager;
        if (!manager || manager.id == null) return false;
        const matches = Number(manager.id) === Number(userEmployee.id);
        if (matches) {
          console.log("✅ Found matching manager group:", g.id, g.name);
        }
        return matches;
      });
      managerGroupId = match ? match.id : null;
      console.log("📋 Manager Group ID:", managerGroupId);
    }

    // For group managers with a known permission group, send an object payload
    if (isManager && managerGroupId) {
      const payload = {
        auditTypeId: SURVEY_TYPE.AUDIT,
        organization: orgId,
        groupId: managerGroupId,
        requireGroupId: true,
      };
      console.log("🚀 Dispatching with manager group payload:", payload);
      dispatch({
        type: SG_GET_CULTURE_AUDIT_REPORTS,
        payload,
      });
    } else {
      console.log("📤 Dispatching with fallback string payload");
    // Fallback: keep existing org-wide behavior
      dispatch({
        type: SG_GET_CULTURE_AUDIT_REPORTS,
        payload: `audit-type-id=${SURVEY_TYPE.AUDIT}&organization=${orgId}`,
      });
    }
  }, [dispatch, organizationId, get_culture_audit_reports?.response, get_employee, permissionGroups, permissionGroupsFetched]);

  // Process pulse survey data
  const pulseSurveyData = useMemo(() => {
    if (selectedTab !== 'pulse') return [];
    

    
    if (!allPulseSurveys?.results?.length) return [];

    const accessId = Number(get_auth?.employee?.id);
    
    // Parse survey data similar to LandingPageContext
    const parseSurveyData = (scheduled, templates, accessId) => {
      if (!templates) return [];

      // Task IDs that are scheduled & have assigned employees
      const scheduledTaskIds = scheduled
        ?.filter((task) => task?.assigned_employees?.length > 0)
        ?.map((task) => task?.task_template_user_tasks);

      // Filter out archived or scheduled ones from the drafts
      const filteredTemplates = templates
        ?.filter((template) => !scheduledTaskIds?.includes(template?.id))
        .filter((template) => template?.status !== 99);

      // Filter out the archived ones from the scheduled tasks
      const archivedTemplates = templates
        ?.filter((template) => template?.status === 99)?.map((template) => template?.id);

      // Filter out the archived ones from the scheduled tasks
      const filteredScheduledTasks = scheduled
        ?.filter((task) => {
          return !archivedTemplates?.includes(task?.task_template_user_tasks)
        })
        .filter((task) => task?.status !== 99)?.map((task) => {
          const template = templates?.find((template) => template?.id === task?.task_template_user_tasks);
          const name = template?.name || task?.name;
          return {
            ...task,
            name: name,
          };
        });

      // Map different types of tasks
      const mapDrafts = (templates, accessId) =>
        templates
          ?.filter((template) => {
            // Check if user is the creator
            const isCreator = template?.user_tasks?.find(
              (task) =>
                task?.task_type === "created_by" && task?.user_id === accessId
            );

            // Check if user is a collaborator
            const collaboratorsTask = template?.user_tasks?.find(
              (task) => task?.task_type === "collaborators"
            );
            const isCollaborator = collaboratorsTask?.collaborators?.includes(accessId);

            return isCreator || isCollaborator;
          })
          ?.map((template) => ({
            id: template?.id,
            name: template?.name,
            updatedValue: template?.updated_at,
            updated: new Date(template?.updated_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
                          statusValue: 1,
              status: "Draft",
              type: "draft",
          })) || [];

      const mapSentTasks = (tasks, accessId) => {
        const eligibleWithoutAccess =
          tasks
            ?.filter((task) => {
              const assignedCount = task?.assigned_employees?.length || 0;
              return (
                assignedCount > 0 &&
                (task?.status === 1 || task?.status === 51)
              );
            }) || [];

        const tableRows = eligibleWithoutAccess
          ?.filter((task) => {
            return (
              task?.created_by === accessId ||
              task?.employee_access?.includes(accessId)
            );
          })
          ?.map((task) => ({
            id: task?.id,
            name: task?.name,
            updatedValue: task?.updated_at,
            updated: new Date(task?.updated_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            statusValue: 3,
            status: "Sent",
            type: "sent",
            task_template_user_tasks: task?.task_template_user_tasks,
          })) || [];

        return tableRows;
      };

      const mapCompletedTasks = (tasks, accessId) => {
        const eligibleWithoutAccess =
          tasks
            ?.filter((task) => {
              const assignedCount = task?.assigned_employees?.length || 0;
              return assignedCount > 0 && task?.status === 51;
            }) || [];

        const tableRows = eligibleWithoutAccess
          ?.filter((task) => {
            return (
              task?.created_by === accessId ||
              task?.employee_access?.includes(accessId)
            );
          })
          ?.map((task) => ({
            id: task?.id,
            name: task?.name,
            updatedValue: task?.updated_at,
            updated: new Date(task?.updated_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            statusValue: 51,
            status: "Completed",
            type: "completed",
            task_template_user_tasks: task?.task_template_user_tasks,
          })) || [];


        return tableRows;
      };

      const drafts = mapDrafts(filteredTemplates, accessId);
      const sentTasks = filteredScheduledTasks ? mapSentTasks(filteredScheduledTasks, accessId) : [];
      const completedTasks = filteredScheduledTasks ? mapCompletedTasks(filteredScheduledTasks, accessId) : [];

      // Combine and dedupe by template/id, preferring higher status then latest update
      const dedupedMap = [...completedTasks, ...sentTasks, ...drafts].reduce(
        (map, task) => {
          const key = task?.id ?? task?.task_template_user_tasks;
          const existing = map.get(key);
          if (!existing) {
            map.set(key, task);
            return map;
          }

          const existingStatus = existing?.statusValue ?? 0;
          const nextStatus = task?.statusValue ?? 0;
          const existingDate = existing?.updatedValue ? new Date(existing.updatedValue) : new Date(0);
          const nextDate = task?.updatedValue ? new Date(task.updatedValue) : new Date(0);

          const shouldReplace =
            nextStatus > existingStatus ||
            (nextStatus === existingStatus && nextDate > existingDate);

          if (shouldReplace) {
            map.set(key, task);
          }
          return map;
        },
        new Map()
      );

      const dedupedTasks = Array.from(dedupedMap.values());

      dedupedTasks.sort(
        (a, b) => new Date(b.updatedValue) - new Date(a.updatedValue)
      );

      return dedupedTasks;
    };

    try {
      const result = parseSurveyData(
        scheduledPulseSurveys?.results,
        allPulseSurveys?.results,
        accessId
      )?.filter((s) => s?.status !== "Draft");
      
      return result;
    } catch (error) {
      console.error("Error parsing pulse survey data:", error);
      return [];
    }
  }, [selectedTab, allPulseSurveys, scheduledPulseSurveys, get_auth]);


  console.log(pulseSurveyData)

  // Filter pulse survey data by search
  const filteredPulseData = pulseSearchValue
    ? pulseSurveyData.filter((row) =>
        Object.values(row).some((value) => {
          if (typeof value === "string") {
            return value.toLowerCase().includes(pulseSearchValue.toLowerCase());
          }
          return false;
        })
      )
    : pulseSurveyData;

  // Process survey data by sort order (Latest Data)
  const surveyDataBySortOrder = useMemo(() => {
    
    if (!get_culture_audit_reports?.response?.length) return [];

    const sortedResponses = [...get_culture_audit_reports.response].sort((a, b) => b.id - a.id);

    // Group by sort order
    const grouped = sortedResponses.reduce((acc, response) => {
      const sortOrder = response.question?.sort_order;
      if (!sortOrder) return acc;

      if (!acc[sortOrder]) {
        acc[sortOrder] = {
          sortOrder,
          questionTitle: response.question?.name || `Survey ${sortOrder}`,
          responses: [],
          latestDate: null,
          latestResponse: null,
        };
      }
      
      acc[sortOrder].responses.push(response);
      
      // Track latest date
      const responseDate = new Date(response.date);
      if (!acc[sortOrder].latestDate || responseDate > acc[sortOrder].latestDate) {
        acc[sortOrder].latestDate = responseDate;
        acc[sortOrder].latestResponse = response;
      }
      
      return acc;
    }, {});

    // Convert to array and get latest data counts
    return Object.values(grouped).map(group => {
      const { latestResponse, ...baseGroup } = group;
      const primaryResponse = latestResponse ?? baseGroup.responses[0];

      let closeDateValue = null;

      if (primaryResponse) {
        const responseId = extractNumericId(primaryResponse?.id);
        const directSchedule = responseId != null ? scheduleMapById.get(responseId) : null;

        closeDateValue =
          directSchedule?.close_at ??
          primaryResponse?.close_at ??
          primaryResponse?.close_date ??
          null;

        if (!closeDateValue) {
          const fallbackScheduleId = pickFirstNumericId(
            primaryResponse?.debrief_schedule,
            primaryResponse?.debrief_schedule_id,
            primaryResponse?.schedule,
            primaryResponse?.schedule_id,
            primaryResponse?.survey_schedule,
            primaryResponse?.survey_schedule_id,
            primaryResponse?.survey_event,
            primaryResponse?.survey_event_id
          );

          if (fallbackScheduleId != null) {
            const fallbackSchedule = scheduleMapById.get(fallbackScheduleId);
            if (fallbackSchedule?.close_at) {
              closeDateValue = fallbackSchedule.close_at;
            }
          }
        }

        if (!closeDateValue && primaryResponse?.date) {
          closeDateValue = primaryResponse.date;
        }
      }

      const closeDateObj = closeDateValue ? new Date(closeDateValue) : null;
      const effectiveLatestDate = closeDateObj ?? baseGroup.latestDate;
      const formattedDate =
        effectiveLatestDate?.toLocaleDateString() || 'N/A';

      return {
        ...baseGroup,
        latestDate: effectiveLatestDate,
        closeDate: closeDateValue,
        participantCount: baseGroup.responses?.length || 0,
        surveySubmissions: baseGroup.responses.length,
        formattedDate,
        questionCount: primaryResponse?.questions?.length || 0,
      };
    }).sort((a, b) => b.latestDate - a.latestDate);
  }, [reportsLength, hasOrganizations, scheduleMapById]);

  // Process individual survey debriefs (Historical Data)
  const surveyDebriefsByGroup = useMemo(() => {
    if (!get_culture_audit_reports?.response?.length) return [];

    const sortedResponses = [...get_culture_audit_reports.response].sort((a, b) => b.id - a.id);

    return sortedResponses.map((response) => {

      const sortOrder = response.question?.sort_order;
      const responseId = extractNumericId(response?.id);
      const directSchedule = responseId != null ? scheduleMapById.get(responseId) : null;
      const closeDate = directSchedule?.close_at ?? response.date;
      const formattedDate = closeDate ? new Date(closeDate).toLocaleDateString() : 'N/A';

      const fallbackScheduleId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );

      const resolvedDebriefScheduleId =
        directSchedule ? responseId : fallbackScheduleId;

      const scheduleGroupMeta = resolvedDebriefScheduleId != null
        ? scheduleToGroupIdMap.get(resolvedDebriefScheduleId)
        : null;

      const scheduleGroupId = scheduleGroupMeta?.groupId ?? (directSchedule
        ? pickFirstNumericId(
            directSchedule?.organization_group,
            directSchedule?.organization_group?.id,
            directSchedule?.organization_group_id,
            directSchedule?.group,
            directSchedule?.group_id
          )
        : null);

      const fallbackGroupId = pickFirstNumericId(
        response?.organization_group,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );

      const organizationGroupId = scheduleGroupId ?? fallbackGroupId ?? null;
      const groupNameFromMap = organizationGroupNameMap.get(response?.id);
      const mappedGroupName = organizationGroupId != null
        ? organizationGroupNameMap.get(organizationGroupId)
        : null;

      const organizationGroupName = pickFirstDisplayName(
        response?.name,
        mappedGroupName,
        groupNameFromMap,
        groupNameFromMap?.groupName,
        scheduleGroupMeta?.groupName,
        directSchedule?.organization_group,
        directSchedule?.group_name,
        response?.organization_group_name,
        response?.organization_group,
        response?.group,
        response?.team,
        response?.group_name,
        response?.team_name,
        sortOrder ? `Group ${sortOrder}` : "Group"
      );

      const historyId = responseId ?? resolvedDebriefScheduleId ?? response.id;

      return {
        id: historyId,
        date: response.date,
        formattedDate,
        sortOrder,
        questionTitle: response.question?.name || `Survey ${sortOrder || ""}`.trim(),
        groupTitle: organizationGroupName,
        organizationGroupName,
        organizationGroupId,
        debriefScheduleId: resolvedDebriefScheduleId,
        responses: [response], // Keep as array for compatibility with loadDataSource
        responseCount: 1, // Row represents one survey event record
        respondentCount: Array.isArray(response?.responses) ? response.responses.length : 0,
        requestedCount: Array.isArray(response?.request_emp_id) ? response.request_emp_id.length : 0,
        questionCount: response.questions?.length || 0,
      };
    });
  }, [reportsLength, organizationGroupNameMap, scheduleMapById, scheduleToGroupIdMap]);

  // Load selected data source into Redux
  const loadDataSource = async (dataSource, type, options = {}) => {
    const { seriesSelected = false } = options;
    setLoading(true);
    
    try {
      let responses = [];
      
      if (type === 'sortOrder') {
        responses = get_culture_audit_reports.response.filter(
          r => r.question?.sort_order === dataSource.sortOrder
        );
      } else if (type === 'debrief') {
        responses = dataSource.responses;
      } else if (type === 'debriefMulti') {
        responses = dataSource.responses;
      }
      
      // Apply personality conversion if needed
      if (checkPersonality(get_organizations)) {
        responses = convert_personality_to_categories(responses);
      }
      
      // Process the data similar to LeaderDataLoader
      let sorted_responses = responses.sort((a, b) => b.id - a.id);
      let most_recent = get_most_recent(sorted_responses);
      // Collect metadata-centric identifiers for downstream consumers
      const primaryResponse = Array.isArray(responses) ? responses[0] : null;
      const derivedDebriefScheduleId = pickFirstNumericId(
        dataSource?.debriefScheduleId,
        dataSource?.scheduleId,
        primaryResponse?.debrief_schedule,
        primaryResponse?.debrief_schedule_id,
        primaryResponse?.schedule,
        primaryResponse?.schedule_id,
        primaryResponse?.survey_schedule,
        primaryResponse?.survey_schedule_id,
        primaryResponse?.survey_event,
        primaryResponse?.survey_event_id
      );
      const scheduleGroupMeta = scheduleToGroupIdMap.get(derivedDebriefScheduleId) || null;
      const derivedOrganizationGroupId = pickFirstNumericId(
        dataSource?.organizationGroupId,
        primaryResponse?.organization_group,
        primaryResponse?.organization_group_id,
        primaryResponse?.group,
        primaryResponse?.group_id,
        primaryResponse?.team,
        primaryResponse?.team_id,
        scheduleGroupMeta?.groupId
      );
      const includeGroupMetadata = Boolean(isHistoryTableOpen || dataSource?.forceIncludeGroupTitle);

      const derivedOrganizationGroupName = includeGroupMetadata
        ? pickFirstDisplayName(
            primaryResponse?.name,
            dataSource?.groupTitle,
            dataSource?.organizationGroupName,
            scheduleGroupMeta?.groupName,
            derivedOrganizationGroupId != null ? organizationGroupNameMap.get(derivedOrganizationGroupId) : null,
            primaryResponse?.organization_group_name,
            primaryResponse?.organization_group,
            primaryResponse?.group,
            primaryResponse?.team,
            primaryResponse?.group_name,
            primaryResponse?.team_name,
            dataSource?.name,
            dataSource?.questionTitle,
            dataSource?.question?.name,
            dataSource?.sortOrder ? `Group ${dataSource.sortOrder}` : null,
            "Group"
          )
        : null;

      if (derivedOrganizationGroupId != null) {
        most_recent.organization_group = derivedOrganizationGroupId;
      }
      if (includeGroupMetadata && derivedOrganizationGroupName) {
        most_recent.organization_group_name = derivedOrganizationGroupName;
      }
      if (derivedDebriefScheduleId != null) {
        most_recent.debrief_schedule = derivedDebriefScheduleId;
      }

      // Build inputs used by both methods
      const selectedIds = new Set(sorted_responses.map((r) => r.id));
      const selectedIdsArray = Array.from(selectedIds);
      const allSortedDesc = [...(get_culture_audit_reports.response || [])].sort((a, b) => b.id - a.id);

      // Compute last + historical based on calcMethod
      let historical_data = [];
      if (calcMethod === 'group') {
        most_recent["last"] = get_last_responses_group(allSortedDesc, selectedIdsArray);
        historical_data = sort_historical_data_group(allSortedDesc, selectedIdsArray);
      } else {
        // Cohort method (existing behavior)
        most_recent["last"] = get_last_responses2(allSortedDesc, selectedIdsArray);
        const requestedIds = Array.from(
          new Set(
            sorted_responses
              .map((sr) => sr?.request_emp_id || [])
              .flat()
              .filter((v) => v != null)
          )
        );
        historical_data = sort_historical_data2(sorted_responses.reverse(), requestedIds);
      }

      const processedData = [most_recent, ...historical_data];
      const metadataGroupTitle = includeGroupMetadata ? derivedOrganizationGroupName : null;

      // For "Analyze Latest" (seriesSelected=true), prioritize survey name over survey structure name
      // Survey name comes from response.name, survey structure name comes from questionTitle/question.name
      const surveyNameForMetadata = seriesSelected
        ? (primaryResponse?.name || dataSource.questionTitle || (responses[0]?.question?.title) || `Survey ${dataSource.sortOrder || dataSource.id}`)
        : (dataSource.questionTitle || (responses[0]?.question?.title) || `Survey ${dataSource.sortOrder || dataSource.id}`);

      // Add metadata with survey name and group info
      processedData[0].datasetMetadata = {
          timestamp: Date.now(),
          source: `${type}-${dataSource.sortOrder || dataSource.id}`,
          originalFilteredCount: responses.length,
          selectedSortOrder: dataSource.sortOrder,
          selectionType: type,
          calculationMethod: calcMethod,
        surveyName: surveyNameForMetadata,
          groupTitle: metadataGroupTitle,
          selectionCount: Array.isArray(dataSource.selectedEventIds) ? dataSource.selectedEventIds.length : undefined,
          selectedEventIds: dataSource.selectedEventIds || undefined,
          debriefScheduleId: derivedDebriefScheduleId || undefined,
          organizationGroupId: derivedOrganizationGroupId || undefined,
          organizationGroupName: includeGroupMetadata ? derivedOrganizationGroupName || undefined : undefined,
          seriesSelected: Boolean(seriesSelected),
        };
        
        if (outcomesQuestionsList?.length > 0) {
          let outcomeSortOrder = null;
          
          // Priority 1: Check for outcome_question in the filtered survey responses' additional_questions
          if (responses?.length > 0) {
            // Look for outcome_question in the filtered survey responses (could be multiple responses)
            // Use the first response that has additional_questions.outcome_question defined
            const responseWithOutcome = responses.find(response => 
              response?.additional_questions?.outcome_question?.question?.sort_order
            );
            
            if (responseWithOutcome) {
              outcomeSortOrder = responseWithOutcome.additional_questions.outcome_question.question.sort_order;
            }
          }
          
          // Priority 2: Fallback to get_organizations configuration
          if (!outcomeSortOrder && get_organizations && organizationId) {
            outcomeSortOrder = get_organizations?.[organizationId]?.styling?.survey_sequence?.find(
              (f) => f.value === "outcome_question"
            )?.question?.sort_order;
          }
          
          if (outcomeSortOrder) {
            const outcomeQ = outcomesQuestionsList.find(
              (f) => f.sort_order === outcomeSortOrder
            );
            
            
            // Store outcome questions in metadata for Dashboard to use
            processedData[0].datasetMetadata.outcomeQuestions = outcomeQ;
          } else {
          }
        }
        
        // Dispatch to Redux
        dispatch({
          type: SET_RAW_DATA,
          payload: processedData,
        });
        
        dispatch({
          type: SET_DATA_DATES,
          payload: get_dates(historical_data),
        });
        
        // Set up navigation state if we have survey structure
        if (get_survey_structure?.[0]) {
          const DefState = build_default_State(get_survey_structure[0]);
          dispatch({
            type: SET_NAVIGATION,
            payload: DefState,
          });
          
          // Apply initial filtering
          if (nav_state && Object.keys(nav_state).length > 0) {
            let filtered_data = filter_data(
              processedData,
              nav_state,
              get_survey_structure[0]
            );
            
            dispatch({
              type: SET_FILTERED_DATA,
              payload: filtered_data.filter_data,
            });
            
            dispatch({
              type: SET_FILTERED_LAST_DATA,
              payload: filtered_data.filter_data_last,
            });
          }
        }


        
        setSelectedDataSource({
          ...dataSource,
          type,
          questionTitle: seriesSelected ? surveyNameForMetadata : dataSource.questionTitle,
          groupTitle: metadataGroupTitle,
        });
        
        // Generate dataset identifier and redirect to analytics dashboard
        const datasetId = generateDatasetId(dataSource, type);
        navigate(`/leader/audit/new-analytics/dashboard/${datasetId}`);
    } catch (error) {
      console.error('Error loading data source:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to pulse survey analytics
  const navigateToPulseAnalytics = (pulseSurvey) => {
    navigate(`/leader/audit/schedule-v2/pulse/view/${pulseSurvey.id}`);
  };

  // Open inline history table for a given survey sort order
  const openHistoryForSortOrder = (sortOrder, questionTitle) => {
    try {
      const items = (surveyDebriefsByGroup || [])
        .filter((it) => it.sortOrder === sortOrder)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setHistoryItems(items);
      setHistorySurveyName(questionTitle || `Survey ${sortOrder}`);
      setHistorySortOrder(sortOrder);
      setSelectedHistoryIds(new Set());
      setIsHistoryTableOpen(true);
      setHistoryPage(1);
    } catch (e) {
      console.error('Error opening history table:', e);
    }
  };

  // Toggle a single history item selection
  const toggleHistorySelection = (id) => {
    if (!isMultiSelectMode) return;
    setSelectedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Select all history items
  const selectAllHistory = () => {
    if (!isMultiSelectMode) return;
    setSelectedHistoryIds(new Set((historyItems || []).map((it) => it.id)));
  };

  // Clear all selections
  const clearHistorySelection = () => {
    setSelectedHistoryIds(new Set());
  };

  // Toggle select all via header checkbox
  const handleToggleSelectAllHistory = () => {
    if (!isMultiSelectMode) return;
    const total = (historyItems || []).length;
    if (!total) return;
    if (selectedHistoryIds.size === total) {
      clearHistorySelection();
    } else {
      selectAllHistory();
    }
  };

  // Select items within 30 days from the latest entry
  const selectWithin30Days = () => {
    if (!isMultiSelectMode) return;
    try {
      if (!historyItems?.length) return;
      const latestDate = historyItems.reduce((max, it) => {
        const d = new Date(it?.date);
        return d > max ? d : max;
      }, new Date(0));
      const cutoff = new Date(latestDate);
      cutoff.setDate(cutoff.getDate() - 30);
      const ids = historyItems
        .filter((it) => new Date(it?.date) >= cutoff)
        .map((it) => it.id);
      setSelectedHistoryIds(new Set(ids));
    } catch (e) {
      console.error('Error in selectWithin30Days:', e);
    }
  };

  // Analyze the currently selected history items (multi-select)
  const analyzeSelectedHistory = () => {
    const selected = (historyItems || []).filter((it) => selectedHistoryIds.has(it.id));
    if (!selected.length) return;

    const combined = {
      id: `multi-${Date.now()}`,
      sortOrder: historySortOrder,
      questionTitle: historySurveyName,
      responses: selected.flatMap((it) => it.responses || []),
      selectedEventIds: selected.map((it) => it.id),
    };

    loadDataSource(combined, 'debriefMulti');
  };

  // Analyze a single history item immediately (no intermediate selection state needed)
  const analyzeSingleHistory = (hItem) => {
    if (!hItem) return;
    loadDataSource(hItem, 'debrief');
  };

  
  // Generate a unique identifier for the dataset
  const generateDatasetId = (dataSource, type) => {
    if (type === 'sortOrder') {
      return `sort-${dataSource.sortOrder}-latest-${Date.now()}`;
    } else if (type === 'debrief') {
      const dateStr = dataSource.date ? new Date(dataSource.date).toISOString().split('T')[0] : 'single';
      return `debrief-${dateStr}-${dataSource.sortOrder}-${Date.now()}`;
    } else if (type === 'debriefMulti') {
      const count = Array.isArray(dataSource.selectedEventIds) ? dataSource.selectedEventIds.length : (dataSource.responses?.length || 0);
      return `multi-${dataSource.sortOrder}-${count}-${Date.now()}`;
    }
    return `dataset-${Date.now()}`;
  };

  const allData = selectedTab === 'latest' ? surveyDataBySortOrder : filteredPulseData;
  
  // Pagination logic
  const totalPages = Math.ceil(allData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = allData.slice(startIndex, endIndex);

  // History table pagination
  const historyTotalPages = Math.ceil((historyItems || []).length / historyItemsPerPage) || 1;
  const historyStartIndex = (historyPage - 1) * historyItemsPerPage;
  const historyEndIndex = historyStartIndex + historyItemsPerPage;
  const historyCurrentData = (historyItems || []).slice(historyStartIndex, historyEndIndex);
  const totalHistoryCount = (historyItems || []).length;
  const allHistorySelected = totalHistoryCount > 0 && selectedHistoryIds.size === totalHistoryCount;
  const hasPartialHistorySelection = selectedHistoryIds.size > 0 && !allHistorySelected;
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const shouldShowAnalyzeSelected = isMultiSelectMode && selectedHistoryIds.size > 0;
  

  // Return to the main survey table
  const backToMainTable = () => {
    setIsHistoryTableOpen(false);
    setHistoryItems([]);
    setSelectedHistoryIds(new Set());
    setHistorySortOrder(null);
    setIsMultiSelectMode(false);
  };
  
  // Toggle multi-select mode
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode((prev) => {
      if (prev) {
        setSelectedHistoryIds(new Set());
        if (historySelectAllRef.current) {
          historySelectAllRef.current.checked = false;
          historySelectAllRef.current.indeterminate = false;
        }
      }
      return !prev;
    });
  };
  const showMainPagination = totalPages > 1;
  const showHistoryPagination = historyTotalPages > 1;

  // Determine if any culture audit schedule is live (status === 3)
  const hasAnyLiveSchedule = scheduleResults.some((s) => Number(s?.status) === 3);

  const findMatchingSchedule = (historyItem) => {
    if (!historyItem?.date || scheduleResults.length === 0) return null;
    const directScheduleId = pickFirstNumericId(
      historyItem?.debriefScheduleId,
      historyItem?.scheduleId,
      historyItem?.responses?.[0]?.debrief_schedule,
      historyItem?.responses?.[0]?.debrief_schedule_id,
      historyItem?.responses?.[0]?.schedule,
      historyItem?.responses?.[0]?.schedule_id,
      historyItem?.responses?.[0]?.survey_schedule,
      historyItem?.responses?.[0]?.survey_schedule_id,
      historyItem?.responses?.[0]?.survey_event,
      historyItem?.responses?.[0]?.survey_event_id
    );
    if (directScheduleId != null) {
      const exactMatch = scheduleMapById.get(directScheduleId);
      if (exactMatch) {
        return exactMatch;
      }
    }
    const eventTime = new Date(historyItem.date).getTime();
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (const sched of scheduleResults) {
      const start = sched?.scheduled_for ? new Date(sched.scheduled_for).getTime() : null;
      const end = sched?.close_at ? new Date(sched.close_at).getTime() : null;

      // Prefer schedules that contain the event time
      if (start && end && eventTime >= start && eventTime <= end) {
        best = sched;
        bestScore = -1; // exact range match
        break;
      }

      // Otherwise choose closest boundary (start or end)
      let score = Number.POSITIVE_INFINITY;
      if (start != null) score = Math.min(score, Math.abs(eventTime - start));
      if (end != null) score = Math.min(score, Math.abs(eventTime - end));
      if (score < bestScore) {
        best = sched;
        bestScore = score;
      }
    }
    return best;
  };

  const getParticipantsForHistory = (historyItem) => {
    const sched = findMatchingSchedule(historyItem);
    const participants = Number(sched?.total_requests) || 0;
    return participants;
  };

  const yourGroupDisplay = yourGroupName || "—";

  useEffect(() => {
    const nextName = yourGroupName || null;
    const nextId = yourGroupId ?? null;
    const last = lastUserGroupRef.current;

    if (!nextName && nextId == null) {
      return;
    }

    if (last.name === nextName && last.id === nextId) {
      return;
    }

    lastUserGroupRef.current = { name: nextName, id: nextId };

    dispatch({
      type: SET_CURRENT_USER_GROUP,
      payload: {
        name: nextName,
        id: nextId,
        memberCount: yourGroupCount ?? null,
        source: "new-analytics",
      },
    });
  }, [dispatch, yourGroupCount, yourGroupId, yourGroupName]);

  // Reset pagination when switching tabs and update URL
  useEffect(() => {
    setCurrentPage(1);
    
    // Update URL with current tab
    const newSearchParams = new URLSearchParams(searchParams);
    if (selectedTab === 'latest') {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', selectedTab);
    }
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  }, [selectedTab, navigate, searchParams]);

  // Clear selected data source when component mounts (when returning to analytics table)
  useEffect(() => {
    setSelectedDataSource(null);
  }, []);

  // Fetch debrief schedules for Engagement/Culture Audit status mapping
  useEffect(() => {
    if (!organizationId) return;
    const payload = `type_of=${SURVEY_TYPE.AUDIT}&organization=${organizationId}`;
    dispatch({ type: SG_GET_DEBRIEF_SCHEDULES, payload });
  }, [dispatch, organizationId]);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (!historySelectAllRef.current) return;
    if (!isMultiSelectMode) {
      historySelectAllRef.current.indeterminate = false;
      historySelectAllRef.current.checked = false;
      return;
    }
    historySelectAllRef.current.indeterminate = hasPartialHistorySelection;
  }, [hasPartialHistorySelection, isMultiSelectMode]);

  return (
    <Container isInitialLoad={isInitialLoad}>
      <AnimatedSection delay={0}>
        <AnalyticsBreadcrumb 
          groupTitle={selectedDataSource?.groupTitle}
          isHistoryView={isHistoryTableOpen}
          historySurveyName={historySurveyName}
          onNavigateToAnalytics={backToMainTable}
        />
      </AnimatedSection>
      
      <AnimatedSection delay={100}>
        {isHistoryTableOpen ? (
          <HeaderSection>
            <Title>{historySurveyName ? `${historySurveyName} - Series History` : 'Series History'}</Title>
          </HeaderSection>
        ) : (
          <HeaderSection>
            <Title>
              <TitleIconWrapper aria-hidden>
                <DashboardIcon />
              </TitleIconWrapper>
              Survey Analytics
            </Title>
            <Subtitle>Analyze your surveys and gain valuable insights through AI-powered analysis.</Subtitle>
          </HeaderSection>
        )}
      </AnimatedSection>

      {selectedDataSource && (
        <AnimatedSection delay={200}>
          <SelectedDataCard>
            <SelectedDataTitle>Currently Loaded Dataset</SelectedDataTitle>
            <SelectedDataName>{selectedDataSource.questionTitle || selectedDataSource.formattedDate}</SelectedDataName>
            <SelectedDataMeta>
              {selectedDataSource.type === 'sortOrder' 
                ? `Latest data from Sort Order ${selectedDataSource.sortOrder}`
                : `Specific debrief from ${selectedDataSource.formattedDate}`
              }
            </SelectedDataMeta>
          </SelectedDataCard>
        </AnimatedSection>
      )}

      {!isHistoryTableOpen && (
        <AnimatedSection delay={selectedDataSource ? 300 : 200}>
          <TabContainer>
            <TabButton 
              active={selectedTab === 'latest'} 
              onClick={() => setSelectedTab('latest')}
            >
              Engagement Surveys
            </TabButton>
            <TabButton 
              active={selectedTab === 'pulse'} 
              onClick={() => setSelectedTab('pulse')}
            >
              Pulse Surveys
            </TabButton>
          </TabContainer>
        </AnimatedSection>
      )}

      {selectedTab === 'pulse' && (
        <AnimatedSection delay={selectedDataSource ? 350 : 250}>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search pulse surveys..."
              value={pulseSearchValue}
              onChange={(e) => setPulseSearchValue(e.target.value)}
            />
            {pulseSearchValue && (
              <ClearButton onClick={() => setPulseSearchValue("")}>
                ×
              </ClearButton>
            )}
          </SearchContainer>
        </AnimatedSection>
      )}

      <AnimatedSection delay={selectedDataSource ? 400 : 300}>
        {!isHistoryTableOpen ? (
          <TableCard>
            <TableWrapper>
              <TableContainer>
                <SurveysTable>
                  <TableHeader>
                    <TableHeaderRow>
                      {selectedTab === 'pulse' ? (
                        <>
                          <TableHeaderCell width="28%">Survey Name</TableHeaderCell>
                          <TableHeaderCell width="18%">Your Group</TableHeaderCell>
                          <TableHeaderCell width="14%">Total Respondents</TableHeaderCell>
                          <TableHeaderCell width="18%">Last Updated</TableHeaderCell>
                          <TableHeaderCell width="13%">Status</TableHeaderCell>
                          <TableHeaderCell width="21%" maxWidth="280px" minWidth="210px">
                            Actions
                          </TableHeaderCell>
                        </>
                      ) : (
                        <>
                          <TableHeaderCell width="28%">Survey Series</TableHeaderCell>
                          <TableHeaderCell width="18%">Your Group</TableHeaderCell>
                            <TableHeaderCell width="14%">Total respondents</TableHeaderCell>
                            <TableHeaderCell width="20%" maxWidth="260px" minWidth="200px">
                              Actions
                            </TableHeaderCell>
                        </>
                      )}
                    </TableHeaderRow>
                  </TableHeader>

                  <TableBody>
                    {currentData.map((item, index) => {
                      const isLastRow = index === currentData.length - 1;
                      const suppressBorder = showMainPagination && isLastRow;
                      return (
                        <ClickableTableRow
                          key={selectedTab === 'latest' ? item.sortOrder : item.id}
                          isSelected={selectedTab === 'latest'
                            ? selectedDataSource?.sortOrder === item.sortOrder && selectedDataSource?.type === 'sortOrder'
                            : false}
                          delay={(selectedDataSource ? 500 : 400) + index * 50}
                        >
                          {selectedTab === 'pulse' ? (
                            <>
                              <TableCell width="28%" noBorder={suppressBorder}>
                                <DataSourceName>{item.name}</DataSourceName>
                              </TableCell>
                              <TableCell width="18%" noBorder={suppressBorder}>
                                <GroupNameText>
                                  {yourGroupName}
                                </GroupNameText>
                              </TableCell>
                              <TableCell width="14%" noBorder={suppressBorder}>
                                <DataValue>{yourGroupCount != null ? yourGroupCount : "—"}</DataValue>
                              </TableCell>
                              <TableCell width="18%" noBorder={suppressBorder}>
                                <DateText>Updated: {item.updated}</DateText>
                              </TableCell>
                              <TableCell width="13%" noBorder={suppressBorder}>
                                <StatusBadge status={item.statusValue}>{item.status}</StatusBadge>
                              </TableCell>
                              <TableCell
                                width="21%"
                                maxWidth="280px"
                                minWidth="210px"
                                noBorder={suppressBorder}
                              >
                                <ActionContainer>
                                  <AnalyzeButton
                                    onClick={() => navigateToPulseAnalytics(item)}
                                    disabled={loading || (isMultiSelectMode && selectedHistoryIds.size > 0)}
                                  >
                                    Analyze
                                  </AnalyzeButton>
                                </ActionContainer>
                              </TableCell>
                            </>
                          ) : (
                            <>
                                <TableCell width="28%" noBorder={suppressBorder}>
                                  <DataSourceName>{item.questionTitle}</DataSourceName>
                                </TableCell>
                                <TableCell width="18%" noBorder={suppressBorder}>
                                  <GroupNameText>
                                    {yourGroupName}
                                  </GroupNameText>
                                </TableCell>
                                <TableCell width="14%" noBorder={suppressBorder}>
                                  <DataValue>{yourGroupCount != null ? yourGroupCount : "—"}</DataValue>
                                </TableCell>
                                <TableCell
                                  width="20%"
                                  maxWidth="260px"
                                  minWidth="200px"
                                  noBorder={suppressBorder}
                                >
                                  <ActionContainer>
                                    <AnalyzeButton
                                      onClick={() => {
                                        const latestItem = (surveyDebriefsByGroup || [])
                                          .filter((it) => it.sortOrder === item.sortOrder)
                                          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                                        if (latestItem) {
                                          loadDataSource(latestItem, 'debrief', {
                                            seriesSelected: true,
                                          });
                                        }
                                      }}
                                      disabled={loading || (isMultiSelectMode && selectedHistoryIds.size > 0)}
                                    >
                                      {isHistoryTableOpen ? "Analyze" : "Analyze"}
                                    </AnalyzeButton>
                                    <SecondaryButton
                                      onClick={() => openHistoryForSortOrder(item.sortOrder, item.questionTitle)}
                                      disabled={loading}
                                    >
                                      History
                                    </SecondaryButton>
                                  </ActionContainer>
                                </TableCell>
                            </>
                          )}
                        </ClickableTableRow>
                      );
                    })}
                  </TableBody>
                </SurveysTable>
              </TableContainer>
            </TableWrapper>

            {showMainPagination && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {startIndex + 1} to {Math.min(endIndex, allData.length)} of {allData.length} entries
                </PaginationInfo>
                
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PaginationButton>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first page, last page, current page and 2 pages around current
                      return page === 1 || 
                             page === totalPages || 
                             Math.abs(page - currentPage) <= 2;
                    })
                    .map((page, index, visiblePages) => {
                      // Add ellipsis if there's a gap
                      const prevPage = visiblePages[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <PaginationEllipsis>...</PaginationEllipsis>}
                          <PaginationButton
                            onClick={() => handlePageChange(page)}
                            active={page === currentPage}
                          >
                            {page}
                          </PaginationButton>
                        </React.Fragment>
                      );
                    })}
                  
                  <PaginationButton 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </TableCard>
        ) : (
          <>
            <HeaderSection>
              {/*
              <HeaderTopRow>
                <MethodInline>
                  <MethodLabel>Last results method</MethodLabel>
                  <MethodSelectInline value={calcMethod} onChange={(e) => setCalcMethod(e.target.value)}>
                    <option value="cohort">Cohort method</option>
                    <option value="group">Group method</option>
                  </MethodSelectInline>
                </MethodInline>
              </HeaderTopRow>
              */}
              <HistoryActionContainer style={{ marginTop: 12 }}>
                <MultiSelectToggleButton 
                  onClick={toggleMultiSelectMode}
                  isActive={isMultiSelectMode}
                >
                  {isMultiSelectMode ? '✕ Exit Multi-Select' : '+ Analyze Multiple'}
                </MultiSelectToggleButton>
                {isMultiSelectMode && (
                  <MultiSelectActions>
                    <AnalyzeButton 
                      onClick={analyzeSelectedHistory}
                      disabled={selectedHistoryIds.size === 0}
                    >
                      Analyze Selected ({selectedHistoryIds.size})
                    </AnalyzeButton>
                  </MultiSelectActions>
                )}
              </HistoryActionContainer>
            </HeaderSection>

            <TableCard>
              <TableWrapper>
                <TableContainer>
                  <SurveysTable>
                    <TableHeader>
                      <TableHeaderRow>
                        {isMultiSelectMode && (
                          <TableHeaderCell width="8%">
                            <CheckboxWrapper>
                              <StyledCheckbox
                                type="checkbox"
                                ref={historySelectAllRef}
                                checked={allHistorySelected}
                                onChange={handleToggleSelectAllHistory}
                                disabled={totalHistoryCount === 0}
                                aria-label="Select all history entries"
                              />
                            </CheckboxWrapper>
                          </TableHeaderCell>
                        )}
                        <TableHeaderCell width={isMultiSelectMode ? "45%" : "53%"}>
                          Survey Name
                        </TableHeaderCell>
                        <TableHeaderCell width="20%">Close Date</TableHeaderCell>
                        <TableHeaderCell width="12%">Compliance</TableHeaderCell>
                        <TableHeaderCell width="15%">
                          &nbsp;
                        </TableHeaderCell>
                      </TableHeaderRow>
                    </TableHeader>
                    <TableBody>
                      {historyCurrentData.map((hItem, index) => {
                        const checked = selectedHistoryIds.has(hItem.id);
                        const isLastRow = index === historyCurrentData.length - 1;
                        const suppressBorder = showHistoryPagination && isLastRow;
                        const globalIndex = historyStartIndex + index;
                        const roundNumber = Math.max(totalHistoryCount - globalIndex, 1);
                        const roundLabel = `Round #${roundNumber}`;
                        const scheduleId = pickFirstNumericId(
                          hItem?.debriefScheduleId,
                          hItem?.scheduleId,
                          hItem?.responses?.[0]?.debrief_schedule,
                          hItem?.responses?.[0]?.debrief_schedule_id,
                          hItem?.responses?.[0]?.schedule,
                          hItem?.responses?.[0]?.schedule_id,
                          hItem?.responses?.[0]?.survey_schedule,
                          hItem?.responses?.[0]?.survey_schedule_id,
                          hItem?.responses?.[0]?.survey_event,
                          hItem?.responses?.[0]?.survey_event_id
                        );
                        const scheduleGroupMeta = scheduleToGroupIdMap.get(scheduleId) || null;
                        const derivedGroupId = pickFirstNumericId(
                          hItem?.organizationGroupId,
                          scheduleGroupMeta?.groupId
                        );
                        const mappedGroupName =
                          scheduleGroupMeta?.groupName ||
                          (derivedGroupId != null
                            ? organizationGroupNameMap.get(derivedGroupId)
                            : undefined);
                        const displayGroupName =
                          hItem.groupTitle ||
                          mappedGroupName ||
                          hItem.organizationGroupName ||
                          hItem.questionTitle;
                        const primaryLabel = displayGroupName || roundLabel ;
                        return (
                          <ClickableTableRow key={hItem.id} delay={400 + index * 30}>
                            {isMultiSelectMode && (
                              <TableCell width="8%" noBorder={suppressBorder}>
                                <CheckboxWrapper>
                                  <StyledCheckbox
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleHistorySelection(hItem.id)}
                                    aria-label={`Select ${displayGroupName}`}
                                  />
                                </CheckboxWrapper>
                              </TableCell>
                            )}
                            <TableCell width={isMultiSelectMode ? "45%" : "53%"} noBorder={suppressBorder}>
                              <DataSourceName>{primaryLabel}</DataSourceName>
                            </TableCell>
                          <TableCell width="20%" noBorder={suppressBorder}>
                              <DateText>{hItem.formattedDate}</DateText>
                            </TableCell>
                            <TableCell width="12%" noBorder={suppressBorder}>
                              {(() => {
                                const participantsPrimary = Number(hItem.requestedCount) || 0;
                                const participantsFallback = getParticipantsForHistory(hItem) || 0;
                                const participants = participantsPrimary > 0 ? participantsPrimary : participantsFallback;
                                const responses = Number(hItem.respondentCount) || 0;

                                // If no completed responses, show N/A for the column
                                if (responses === 0) {
                                  return <DataValue>N/A</DataValue>;
                                }

                                if (participants > 0) {
                                  const pct = (responses / participants) * 100;
                                  return (
                                    <ProgressInfo>
                                      <ProgressText>
                                        {responses} / {participants} responses
                                      </ProgressText>
                                      <ProgressBar>
                                        <ProgressFill percentage={pct} />
                                      </ProgressBar>
                                    </ProgressInfo>
                                  );
                                }

                                // Fallback: show response count when participant total is unknown
                                return (
                                  <ProgressInfo>
                                    <ProgressText>
                                      {responses} {responses === 1 ? 'response' : 'responses'}
                                    </ProgressText>
                                  </ProgressInfo>
                                );
                              })()}
                            </TableCell>
                            <TableCell width="15%" noBorder={suppressBorder}>
                              <HistoryAnalyzeButton
                                onClick={() => analyzeSingleHistory(hItem)}
                                disabled={isMultiSelectMode && selectedHistoryIds.size > 0}
                              >
                                Analyze
                              </HistoryAnalyzeButton>
                            </TableCell>
                          </ClickableTableRow>
                        );
                      })}
                    </TableBody>
                  </SurveysTable>
                </TableContainer>
              </TableWrapper>

              {showHistoryPagination && (
                <PaginationContainer>
                  <PaginationInfo>
                    Showing {historyStartIndex + 1} to {Math.min(historyEndIndex, (historyItems || []).length)} of {(historyItems || []).length} entries
                  </PaginationInfo>
                  <PaginationControls>
                    <PaginationButton 
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                    >
                      Previous
                    </PaginationButton>
                    {Array.from({ length: historyTotalPages }, (_, i) => i + 1)
                      .filter(page => page === 1 || page === historyTotalPages || Math.abs(page - historyPage) <= 2)
                      .map((page, index, visiblePages) => {
                        const prevPage = visiblePages[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        return (
                          <React.Fragment key={`hist-${page}`}>
                            {showEllipsis && <PaginationEllipsis>...</PaginationEllipsis>}
                            <PaginationButton
                              onClick={() => setHistoryPage(page)}
                              active={page === historyPage}
                            >
                              {page}
                            </PaginationButton>
                          </React.Fragment>
                        );
                      })}
                    <PaginationButton 
                      onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                      disabled={historyPage === historyTotalPages}
                    >
                      Next
                    </PaginationButton>
                  </PaginationControls>
                </PaginationContainer>
              )}
            </TableCard>
          </>
        )}
      </AnimatedSection>

      {!isHistoryTableOpen && allData.length === 0 && (
        <AnimatedSection delay={selectedDataSource ? 400 : 300}>
          <EmptyState>
            <EmptyIcon>
              <img src={NoData} style={{width:200}}/>
            </EmptyIcon>
            <EmptyTitle>No Surveys Available</EmptyTitle>
            <EmptySubtitle>
              {selectedTab === 'latest' 
                ? 'No engagement surveys found. Please check your engagement survey data or try refreshing.'
                : 'No pulse surveys found. Please check your pulse survey data or try refreshing.'
              }
            </EmptySubtitle>
          </EmptyState>
        </AnimatedSection>
      )}

    </Container>
  );
};

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Animated wrapper component
const AnimatedSection = styled.div`
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: ${props => props.delay || 0}ms;
  animation-fill-mode: both;
`;

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  opacity: ${props => props.isInitialLoad ? 0 : 1};
  transition: opacity 0.3s ease-in;
`;

const HeaderSection = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #F8F9FA;
  border-radius: 7px;
  color: #1F2937;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6B7280;
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
`;

const SelectedDataCard = styled.div`
  background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 32px;
  color: white;
  transform: translateY(0);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const SelectedDataTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 4px;
`;

const SelectedDataName = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const SelectedDataMeta = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #F3F4F6;
  margin-bottom: 24px;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.active ? '#3B82F6' : '#6B7280'};
  border-bottom: 2px solid ${props => props.active ? '#3B82F6' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    color: #3B82F6;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  @media (max-width: 1024px) {
    margin: 0 -24px;
    padding: 0 24px;
  }
`;

const TableCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: ${scaleIn} 0.4s ease-out;
  display: flex;
  flex-direction: column;
`;

const TableContainer = styled.div`
  width: 100%;
  min-width: 0;
`;

const SurveysTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 0;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 14px 18px;
  text-align: left;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 600;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
  width: ${(props) => props.width || 'auto'};
  max-width: ${(props) => props.maxWidth || 'none'};
  min-width: ${(props) => props.minWidth || '0'};
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRowHighlight = keyframes`
  from {
    opacity: 0;
    transform: translateX(-12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ClickableTableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
  background: ${(props) => (props.isSelected ? '#eef2ff' : '#ffffff')};
  animation: ${TableRowHighlight} 0.35s ease-out both;
  animation-delay: ${(props) => (props.delay ? `${props.delay}ms` : '0ms')};

  &:hover {
    background: #f0f4ff;
    transform: translateX(2px);
    box-shadow: 0 2px 6px rgba(71, 109, 250, 0.08);
  }
`;

const TableCell = styled.td`
  padding: 14px 18px;
  border-bottom: ${(props) => (props.noBorder ? 'none' : '1px solid #f0f0f0')};
  font-family: "Poppins";
  vertical-align: top;
  width: ${(props) => props.width || 'auto'};
  word-break: break-word;
  max-width: ${(props) => props.maxWidth || 'none'};
  min-width: ${(props) => props.minWidth || '0'};

  @media (max-width: 1200px) {
    padding: 16px;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const DataSourceName = styled.div`
  font-family: "Poppins";
  font-size: 14px;
  font-weight: 600;
  color: #414141;
  margin-bottom: 2px;
`;

const GroupNameText = styled.div`
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  color: #111827;
`;

const DataValue = styled.div`
  font-family: "Poppins";
  font-size: 14px;
  color: #7e7e7e;
  font-weight: 500;
`;

// Match ScheduleV2's date styling (see DateText in ScheduleV2)
const DateText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const DataBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  width: fit-content;
  transition: all 0.2s ease;
  font-family: "Poppins";
  
  &:hover {
    transform: scale(1.05);
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: ${props => {
    switch (props.status) {
      case 1:
        return '#F59E0B20';
      case 3:
        return '#3B82F620';
      case 51:
        return '#10B98120';
      default:
        return '#6B728020';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 1:
        return '#F59E0B';
      case 3:
        return '#3B82F6';
      case 51:
        return '#10B981';
      default:
        return '#6B7280';
    }
  }};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  width: fit-content;
  transition: all 0.2s ease;
  font-family: "Poppins";
  
  &:hover {
    transform: scale(1.05);
  }
`;

// ScheduleV2-like status pill for non-pulse surveys
const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  ${(props) => {
    switch (props.variant) {
      case 'active':
        return `background: #e8f5e8; color: #2e7d2e;`;
      case 'completed':
        return `background: #e3f2fd; color: #1976d2;`;
      default:
        return `background: #f0f0f0; color: #666;`;
    }
  }}
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 280px;
  gap: 8px;
  column-gap: 10px;
  flex-wrap: wrap;
  overflow: visible;
  position: relative;
  flex-shrink: 1;

  @media (max-width: 1024px) {
    row-gap: 6px;
  }
`;

const HistoryActionContainer = styled(ActionContainer)`
  justify-content: flex-end;

  @media (max-width: 1200px) {
    justify-content: flex-start;
  }
`;

const MethodLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const MethodInline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MethodSelectInline = styled.select`
  background: #F9FAFB;
  color: #111827;
  border: 1px solid #D1D5DB;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: white; }
`;

const AnalyzeButton = styled.button`
  background: #2D70E2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Poppins";
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  min-width: 96px;
  max-width: 100%;
  flex-shrink: 1;
  
  &:hover:not(:disabled) {
    background: #2563EB;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 1200px) {
    width: 100%;
    justify-content: center;
    min-width: 120px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
    min-width: 100px;
  }
`;

const HistoryAnalyzeButton = styled(AnalyzeButton)`
  width: 85px;
  max-width: 85px;
  padding: 8px 12px;
  justify-content: center;

  @media (max-width: 1200px) {
    width: 85px;
    min-width: 0;
  }

  @media (max-width: 768px) {
    width: 85px;
    min-width: 0;
  }
`;

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  animation: ${scaleIn} 0.6s ease-out 0.2s both;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const EmptySubtitle = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: #F3F4F6;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 24px;
  position: relative;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  border: none;
  background: none;
  padding: 4px 0;
  font-size: 14px;
  color: #374151;
  outline: none;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: #6B7280;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #374151;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #e0e0e0;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6B7280;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PaginationButton = styled.button`
  background: ${props => props.active ? '#3B82F6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border: 1px solid ${props => props.active ? '#3B82F6' : '#D1D5DB'};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  font-family: "Poppins";
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#2563EB' : '#F9FAFB'};
    border-color: ${props => props.active ? '#2563EB' : '#9CA3AF'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: white;
    border-color: #D1D5DB;
    color: #9CA3AF;
  }
`;

const PaginationEllipsis = styled.span`
  padding: 8px 4px;
  color: #6B7280;
  font-size: 14px;
  font-family: "Poppins";
`;

// Progress styles matching ScheduleV2
const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const ProgressText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 2px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #476dfa;
  border-radius: 3px;
  width: ${(props) => Math.min(props.percentage, 100)}%;
  transition: width 0.3s ease;
`;

// Secondary button for lighter actions (e.g., History)
const SecondaryButton = styled.button`
  background: white;
  color: #111827;
  border: 1px solid #D1D5DB;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Poppins";
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 0;
  flex-shrink: 0;
  
  &:hover {
    background: #F9FAFB;
    transform: translateY(-1px);
  }
  
  @media (max-width: 1200px) {
    width: 100%;
    justify-content: center;
    min-width: 120px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
    min-width: 100px;
  }
`;

// Multi-select toggle button with active state
const MultiSelectToggleButton = styled.button`
  background: ${props => props.isActive ? '#3B82F6' : 'white'};
  color: ${props => props.isActive ? 'white' : '#3B82F6'};
  border: 2px solid #3B82F6;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Poppins";
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.isActive ? '#2563EB' : '#EEF2FF'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

// Animated wrapper for multi-select actions
const MultiSelectActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  animation: ${slideInFromLeft} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: left;
`;

// Checkbox wrapper with animation
const CheckboxWrapper = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Styled checkbox
const StyledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3B82F6;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px; /* prevent edge clipping on small screens */
`;

const ModalContent = styled.div`
  background: white;
  width: 860px;
  max-width: 960px;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  overflow: hidden;
  max-height: calc(100vh - 96px);
  animation: ${scaleIn} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #E5E7EB;
  background: #F9FAFB;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #6B7280;
  cursor: pointer;
  padding: 0 4px;
  &:hover { color: #111827; }
`;

const ModalBody = styled.div`
  padding: 12px 20px 20px 20px;
  overflow: auto;
  max-height: calc(100vh - 160px);
`;

const ModalTableHeader = styled.div`
  display: flex;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 8px 8px 0 0;
  padding: 10px 0;
`;

const ModalHeaderCell = styled.div`
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: ${props => props.width};
  flex-shrink: 0;
`;

const ModalTableBody = styled.div`
  border: 1px solid #E5E7EB;
  border-top: none;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
`;

const ModalRow = styled.div`
  display: flex;
  border-bottom: 1px solid #F3F4F6;
  background: white;
  &:last-child { border-bottom: none; }
`;

const ModalCell = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${props => props.width};
  flex-shrink: 0;
`;

export default NewAnalytics;

const DashboardIcon = () => (
  <svg width="18" height="16" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M2 10.5C1.72386 10.5 1.5 10.7239 1.5 11V18C1.5 18.2761 1.72386 18.5 2 18.5H4C4.27614 18.5 4.5 18.2761 4.5 18V11C4.5 10.7239 4.27614 10.5 4 10.5H2ZM0.5 11C0.5 10.1716 1.17157 9.5 2 9.5H4C4.82843 9.5 5.5 10.1716 5.5 11V18C5.5 18.8284 4.82843 19.5 4 19.5H2C1.17157 19.5 0.5 18.8284 0.5 18V11Z" fill="currentColor"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M10 1.5C9.72386 1.5 9.5 1.72386 9.5 2V18C9.5 18.2761 9.72386 18.5 10 18.5H12C12.2761 18.5 12.5 18.2761 12.5 18V2C12.5 1.72386 12.2761 1.5 12 1.5H10ZM8.5 2C8.5 1.17157 9.17157 0.5 10 0.5H12C12.8284 0.5 13.5 1.17157 13.5 2V18C13.5 18.8284 12.8284 19.5 12 19.5H10C9.17157 19.5 8.5 18.8284 8.5 18V2Z" fill="currentColor" />
    <path fill-rule="evenodd" clip-rule="evenodd" d="M18 5.5C17.7239 5.5 17.5 5.72386 17.5 6V18C17.5 18.2761 17.7239 18.5 18 18.5H20C20.2761 18.5 20.5 18.2761 20.5 18V6C20.5 5.72386 20.2761 5.5 20 5.5H18ZM16.5 6C16.5 5.17157 17.1716 4.5 18 4.5H20C20.8284 4.5 21.5 5.17157 21.5 6V18C21.5 18.8284 20.8284 19.5 20 19.5H18C17.1716 19.5 16.5 18.8284 16.5 18V6Z" fill="currentColor" />
  </svg>
);
